"use client";

import { Loader2 } from "lucide-react";
import { useProposalBatch } from "@stockline/web";
import { useTranslation } from "react-i18next";
import { useProposalApprovalActions } from "../../hooks/useProposalApprovalActions";
import { getBatchPrimaryEntityType } from "./proposal-card-shared";
import { ProposalGenericCard } from "./ProposalGenericCard";
import { ProposalGroupPriceCard } from "./ProposalGroupPriceCard";
import { ProposalPreOrderCard } from "./ProposalPreOrderCard";
import { ProposalPriceCard } from "./ProposalPriceCard";

interface ProposalCardProps {
  readonly batchId: string;
  readonly showTimestamp?: boolean;
}

export function ProposalCard({ batchId, showTimestamp }: ProposalCardProps) {
  const { t } = useTranslation();
  const batch = useProposalBatch(batchId);
  const { acting, handleApprove, handleReject } =
    useProposalApprovalActions(batchId);

  if (!batch) {
    return (
      <div className="rounded-lg border border-border p-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("aiProposals.loading")}
      </div>
    );
  }

  const isPending = batch.status === "pending";
  const canAct = isPending && !acting;
  const entityType = getBatchPrimaryEntityType(batch);

  const sharedProps = {
    batch,
    showTimestamp,
    acting,
    canAct,
    onApprove: handleApprove,
    onReject: handleReject,
  };

  if (
    entityType === "pre_order" ||
    entityType === "pre_order_flow" ||
    entityType === "flow"
  ) {
    return <ProposalPreOrderCard {...sharedProps} />;
  }

  if (entityType === "client_group_product_price") {
    return <ProposalGroupPriceCard {...sharedProps} />;
  }

  if (
    entityType === "product_base_price" ||
    entityType === "partner_product_price"
  ) {
    return <ProposalPriceCard {...sharedProps} />;
  }

  return <ProposalGenericCard {...sharedProps} />;
}
